// src/pages/ArticlePage.js
import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';
import axios from 'axios';
import {
  ArrowLeft, User, Clock, Calendar, Share2, Bookmark, Heart, FileText, MessageCircle, Tags
} from 'lucide-react';

import CommentSection from '../components/CommentSection';
import { likeArticle, unlikeArticle, bookmarkArticle, unbookmarkArticle, getArticleStatus, shareArticle } from '../services/articleService';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://animac-metaverse.onrender.com';

// Portal color context
const PortalContext = createContext({ accent: '#a78bfa', accentLight: 'rgba(167, 139, 250,', accentGradient: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%)' });
export const usePortalTheme = () => useContext(PortalContext);

// Detect portal from category
const getPortalColors = (category = '') => {
  const cat = category.toLowerCase();
  const eastKeywords = ['anime', 'japan', 'japanese', 'manga', 'east', 'donghua', 'korean'];
  const westKeywords = ['movie', 'movies', 'tv', 'television', 'film', 'hollywood', 'west', 'series', 'show'];
  
  if (eastKeywords.some(k => cat.includes(k))) {
    return { 
      accent: '#dc2626', 
      accentLight: 'rgba(220, 38, 38,', 
      accentGradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)',
      bgGlow: 'radial-gradient(circle at 20% 80%, rgba(220, 38, 38, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(220, 38, 38, 0.06) 0%, transparent 50%)'
    };
  }
  if (westKeywords.some(k => cat.includes(k))) {
    return { 
      accent: '#06b6d4', 
      accentLight: 'rgba(6, 182, 212,', 
      accentGradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)',
      bgGlow: 'radial-gradient(circle at 20% 80%, rgba(6, 182, 212, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.06) 0%, transparent 50%)'
    };
  }
  // Default purple
  return { 
    accent: '#a78bfa', 
    accentLight: 'rgba(167, 139, 250,', 
    accentGradient: 'linear-gradient(135deg, #a78bfa 0%, #6366f1 50%, #8b5cf6 100%)',
    bgGlow: 'radial-gradient(circle at 20% 80%, rgba(167, 139, 250, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%)'
  };
};

// Shared PageStyles component for consistent styling
const PageStyles = ({ theme }) => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Azonix&display=swap');
    
    :root {
      --az: #a78bfa;
      --jp: #fbbf24;
      --wt: #06b6d4;
      --neu: #8b5cf6;
      --bg: #08080c;
      --glass: rgba(255, 255, 255, 0.03);
      --gb: rgba(167, 139, 250, 0.15);
      --card-bg: linear-gradient(135deg, rgba(20, 20, 30, 0.9), rgba(10, 10, 15, 0.95));
      --accent: ${theme.accent};
      --accent-light: ${theme.accentLight};
      --accent-gradient: ${theme.accentGradient};
    }
    
    * {
      scrollbar-width: thin;
      scrollbar-color: var(--accent) var(--bg);
    }
    
    *::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    
    *::-webkit-scrollbar-track {
      background: var(--bg);
    }
    
    *::-webkit-scrollbar-thumb {
      background: var(--accent);
      border-radius: 4px;
    }
    
    .font-azonix {
      font-family: 'Azonix', sans-serif;
    }
    
    .animate-float {
      animation: float 6s ease-in-out infinite;
    }
    
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    
    .glass-card {
      background: linear-gradient(135deg, rgba(20, 20, 30, 0.85) 0%, rgba(12, 12, 18, 0.9) 100%);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.06);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    }
    
    .gradient-border {
      position: relative;
    }
    
    .gradient-border::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      padding: 1px;
      background: var(--accent-gradient);
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      pointer-events: none;
      opacity: 0.7;
    }
    
    .gradient-text {
      background: var(--accent-gradient);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .gradient-glow {
      background: linear-gradient(135deg, ${theme.accentLight}0.1) 0%, rgba(99, 102, 241, 0.05) 100%);
    }
    
    .section-header {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 24px;
    }
    
    .section-header::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 70%;
      background: var(--accent-gradient);
      border-radius: 2px;
    }
    
    .prose h1, .prose h2, .prose h3, .prose h4 {
      color: #fff !important;
      font-family: 'Azonix', sans-serif;
    }
    
    .prose p {
      color: rgba(255, 255, 255, 0.8) !important;
      line-height: 1.9;
      font-size: 15px;
      margin-bottom: 1.25rem;
    }
    
    .prose a {
      color: var(--accent) !important;
    }
    
    .prose ul, .prose ol {
      color: rgba(255, 255, 255, 0.8) !important;
    }
    
    .prose blockquote {
      border-left: 4px solid var(--accent) !important;
      background: ${theme.accentLight}0.1) !important;
      padding: 1rem !important;
      margin: 1rem 0 !important;
    }
    
    .prose code {
      background: ${theme.accentLight}0.2) !important;
      padding: 0.2rem 0.4rem !important;
      border-radius: 4px !important;
      color: var(--accent) !important;
    }
    
    .prose pre {
      background: rgba(0, 0, 0, 0.5) !important;
      border: 1px solid ${theme.accentLight}0.3) !important;
      border-radius: 8px !important;
    }
    
    .prose img {
      border-radius: 12px !important;
      box-shadow: 0 4px 20px ${theme.accentLight}0.2) !important;
    }
  `}</style>
);

// Get or create unique session ID
const getSessionId = () => {
  let sessionId = localStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [liked, setLiked] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [shared, setShared] = useState(false);
  const [copied, setCopied] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [likeProcessing, setLikeProcessing] = useState(false);
  const [bookmarkProcessing, setBookmarkProcessing] = useState(false);

  // Fetch article and related
  const fetchArticle = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/articles/${slug}`);
      const data = res.data;
      setArticle(data);
      setLikeCount(data.likes ?? 0);
      setBookmarkCount(data.bookmarks ?? 0);
      setShareCount(data.shares ?? 0);

      // Fetch session-specific status
      try {
        const status = await getArticleStatus(data.id, getSessionId());
        setLiked(!!status.liked);
        setBookmarked(!!status.bookmarked);
        setShared(!!status.shared);
      } catch (e) {
        console.log('Could not fetch session status:', e.message);
      }

      // Fetch related articles
      const relRes = await axios.get(`${API_URL}/api/articles`, {
        params: { category: data.category, limit: 3, is_published: true },
      });
      const related = relRes.data.filter(a => a.slug !== slug);
      setRelatedArticles(related);

      setError(null);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      setError('Article not found or failed to load.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Scroll listener for back button
  useEffect(() => {
    const handleScroll = () => {
      const backButton = document.getElementById('back-home-btn');
      if (backButton) backButton.classList.toggle('collapsed', window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Copy link handler
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      
      if (article?.id && !shared) {
        try {
          await shareArticle(article.id, getSessionId());
          const res = await axios.get(`${API_URL}/api/articles/${article.slug}`);
          const status = await getArticleStatus(article.id, getSessionId());
          
          setLikeCount(res.data.likes ?? 0);
          setBookmarkCount(res.data.bookmarks ?? 0);
          setShareCount(res.data.shares ?? 0);
          setLiked(!!status.liked);
          setBookmarked(!!status.bookmarked);
          setShared(!!status.shared);
        } catch (e) {
          console.log('Share already counted for this session');
        }
      }
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Like handler
  const handleLike = async () => {
    if (!article?.id || likeProcessing) return;
    setLikeProcessing(true);
    
    try {
      if (liked) {
        await unlikeArticle(article.id, getSessionId());
      } else {
        await likeArticle(article.id, getSessionId());
      }
      
      const res = await axios.get(`${API_URL}/api/articles/${article.slug}`);
      const status = await getArticleStatus(article.id, getSessionId());
      
      setLikeCount(res.data.likes ?? 0);
      setBookmarkCount(res.data.bookmarks ?? 0);
      setShareCount(res.data.shares ?? 0);
      setLiked(!!status.liked);
      setBookmarked(!!status.bookmarked);
      setShared(!!status.shared);
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setLikeProcessing(false);
    }
  };

  // Bookmark handler
  const handleBookmark = async () => {
    if (!article?.id || bookmarkProcessing) return;
    setBookmarkProcessing(true);
    
    try {
      if (bookmarked) {
        await unbookmarkArticle(article.id, getSessionId());
      } else {
        await bookmarkArticle(article.id, getSessionId());
      }
      
      const res = await axios.get(`${API_URL}/api/articles/${article.slug}`);
      const status = await getArticleStatus(article.id, getSessionId());
      
      setLikeCount(res.data.likes ?? 0);
      setBookmarkCount(res.data.bookmarks ?? 0);
      setShareCount(res.data.shares ?? 0);
      setLiked(!!status.liked);
      setBookmarked(!!status.bookmarked);
      setShared(!!status.shared);
    } catch (err) {
      console.error('Error toggling bookmark:', err);
    } finally {
      setBookmarkProcessing(false);
    }
  };

  // Memoized read time
  const readTime = useMemo(() => {
    if (!article?.content) return 1;
    return Math.ceil(article.content.split(/\s+/).length / 200);
  }, [article]);

  const theme = article ? getPortalColors(article.category || article.category) : getPortalColors('');

  if (loading) return <LoadingScreen />;

  if (error || !article) {
    const defaultTheme = getPortalColors('');
    return (
      <>
        <PageStyles theme={defaultTheme} />
        <div className="min-h-screen relative overflow-hidden" style={{ background: '#08080c' }}>
          <div className="absolute inset-0 gradient-glow"></div>
          <div className="relative z-10 flex items-center justify-center min-h-screen pt-20">
            <div className="glass-card rounded-xl p-8 text-center gradient-border">
              <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: defaultTheme.accent }} />
              <h2 className="text-white font-azonix text-2xl">{error || 'Article Not Found'}</h2>
              <Link to="/" className="mt-4 inline-block hover:underline" style={{ color: defaultTheme.accent }}>
                Return to Home
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageStyles theme={theme} />
      <SEO
        title={article.title}
        description={article.excerpt || 'Read this amazing article on Animac Metaverse.'}
        url={`/article/${article.slug}`}
        image={article.featured_image || '/assets/Outrange black.png'}
        type="article"
        author={article.author}
        publishedTime={article.created_at}
        modifiedTime={article.updated_at}
        section={article.category}
        tags={article.tags || []}
        content={article.content}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="min-h-screen relative overflow-hidden"
        style={{ background: '#08080c' }}
      >
        {/* Background effects */}
        <div className="absolute inset-0 gradient-glow"></div>
        <div className="absolute inset-0" style={{ backgroundImage: theme.bgGlow }}></div>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="relative h-[420px] sm:h-[500px] overflow-hidden"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${article.featured_image || '/default-cover.jpg'})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-[rgba(8,8,12,0.7)] to-transparent z-10" />
          
          <div className="absolute inset-0 z-20 flex items-end justify-center text-center px-6 pb-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="glass-card backdrop-blur-md p-8 rounded-2xl max-w-3xl gradient-border"
            >
              <span 
                className="px-4 py-1 rounded-full text-sm border"
                style={{ 
                  borderColor: `${theme.accent}66`, 
                  color: theme.accent, 
                  background: `${theme.accentLight}0.1)` 
                }}
              >
                {article.category?.toUpperCase() || 'FEATURED'}
              </span>
              <h1 className="text-3xl sm:text-4xl font-exbold font-azonix gradient-text mt-4 mb-2">
                {article.title?.trim()}
              </h1>
              {article.excerpt && (
                <p className="text-lg text-gray-300 italic">{article.excerpt}</p>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Start Divider */}
        <div 
          className="relative flex-1 mx-4 h-1 animate-pulse shadow-lg rounded-full"
          style={{ background: theme.accentGradient }}
        >
          <span 
            className="font-azonix absolute left-1/2 -translate-x-1/2 -top-6 font-bold text-sm md:text-base animate-pulse drop-shadow-lg"
            style={{ color: theme.accent }}
          >
            START
          </span>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <div className="container mx-auto px-4 py-14 max-w-4xl">
            {/* Meta + Actions */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="glass-card p-6 rounded-xl mb-8 gradient-border"
            >
              <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <Link
                  id="back-home-btn"
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium glass-card text-white border transition-all duration-300"
                  style={{ borderColor: `${theme.accent}4d` }}
                >
                  <ArrowLeft size={18} /> Back to Home
                </Link>
              </div>

              <div className="text-sm text-gray-400 flex gap-6 mb-6 flex-wrap">
                <span className="flex items-center gap-2">
                  <User size={14} style={{ color: theme.accent }} /> 
                  <span className="text-gray-300">{article.author || 'Unknown'}</span>
                </span>
                <span className="flex items-center gap-2">
                  <Calendar size={14} style={{ color: theme.accent }} /> 
                  <span className="text-gray-300">{new Date(article.created_at).toLocaleDateString()}</span>
                </span>
                <span className="flex items-center gap-2">
                  <Clock size={14} style={{ color: theme.accent }} /> 
                  <span className="text-gray-300">{readTime} min read</span>
                </span>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <button 
                  onClick={handleLike} 
                  disabled={likeProcessing} 
                  aria-label="Like Article" 
                  className={`flex items-center justify-center w-full h-12 rounded-xl transition-all duration-300 ${
                    liked 
                      ? 'text-pink-500 bg-pink-500/20 border border-pink-500/30' 
                      : 'hover:text-pink-500 hover:bg-pink-500/10 border border-transparent hover:border-pink-500/30'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Heart size={20} fill={liked ? 'currentColor' : 'none'} />
                    <span className="text-xs mt-1">{likeCount}</span>
                  </div>
                </button>
                <button 
                  onClick={handleBookmark} 
                  disabled={bookmarkProcessing} 
                  aria-label="Bookmark Article" 
                  className={`flex items-center justify-center w-full h-12 rounded-xl transition-all duration-300 ${
                    bookmarked 
                      ? 'text-yellow-400 bg-yellow-400/20 border border-yellow-400/30' 
                      : 'hover:text-yellow-400 hover:bg-yellow-400/10 border border-transparent hover:border-yellow-400/30'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Bookmark size={20} fill={bookmarked ? 'currentColor' : 'none'} />
                    <span className="text-xs mt-1">{bookmarkCount}</span>
                  </div>
                </button>
                <button 
                  onClick={handleCopyLink} 
                  aria-label="Share Article" 
                  className={`flex items-center justify-center w-full h-12 rounded-xl transition-all duration-300 ${
                    shared || copied
                      ? 'text-blue-400 bg-blue-400/20 border border-blue-400/30' 
                      : 'hover:text-blue-400 hover:bg-blue-400/10 border border-transparent hover:border-blue-400/30'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <Share2 size={20} fill={shared || copied ? 'currentColor' : 'none'} />
                    <span className="text-xs mt-1">{shareCount}</span>
                  </div>
                </button>
              </div>
            </motion.div>

            {/* Body */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="prose prose-invert max-w-none text-base space-y-8 font-inter"
              style={{ fontSize: '15px', lineHeight: '1.9' }}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content) }}
            />


            {/* End Divider */}
            <div 
              className="mt-14 mb-10 relative flex-1 mx-4 h-1 animate-pulse shadow-lg rounded-full"
              style={{ background: theme.accentGradient }}
            >
              <span 
                className="font-azonix absolute left-1/2 -translate-x-1/2 -top-6 font-bold text-sm md:text-base animate-pulse drop-shadow-lg"
                style={{ color: theme.accent }}
              >
                END
              </span>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
                className="mt-16"
              >
                <h3 className="text-white font-azonix text-2xl mb-6 flex items-center gap-3 section-header">
                  <FileText className="w-6 h-6" style={{ color: theme.accent }} />
                  Related Articles
                </h3>
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {relatedArticles.map((rel, index) => (
                    <motion.div
                      key={rel.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 + index * 0.1, duration: 0.4 }}
                    >
                      <Link 
                        to={`/article/${rel.slug}`} 
                        className="glass-card rounded-xl p-4 block hover:bg-[rgba(167,139,250,0.1)] transition-all duration-300 gradient-border group"
                      >
                        {rel.featured_image && (
                          <div 
                            className="h-40 bg-cover bg-center rounded-lg mb-4 group-hover:scale-105 transition-transform duration-300" 
                            style={{ backgroundImage: `url(${rel.featured_image})` }} 
                          />
                        )}
                        <h4 
                          className="text-lg font-semibold text-white font-azonix transition-colors"
                          style={{ color: theme.accent }}
                        >{rel.title}</h4>
                        <p className="text-gray-400 text-sm mt-2 line-clamp-2">{rel.excerpt}</p>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Tags */}
            {article.tags?.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="mt-12"
              >
                <h3 className="text-white font-azonix text-xl mb-4 flex items-center gap-2">
                  <Tags className="w-5 h-5" style={{ color: theme.accent }} />
                  Tags
                </h3>
                <div className="flex flex-wrap gap-3">
                  {article.tags.map((tag, i) => (
                    <span 
                      key={i} 
                      className="px-4 py-2 rounded-full text-sm glass-card border transition-all duration-300 cursor-pointer hover:opacity-80"
                      style={{ 
                        borderColor: `${theme.accent}4d`, 
                        color: theme.accent 
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Comments */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="mt-12"
            >
              <h3 className="text-white font-azonix text-xl mb-6 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" style={{ color: theme.accent }} />
                Comments
              </h3>
              <div className="glass-card rounded-xl p-6 gradient-border">
                <CommentSection articleId={article.id} />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default ArticlePage;
